import React from "react";
import { translate } from "../../vendors/react-translate/ReactTranslate";
import {
  clearSsoAccountState,
  openMicrosoftLogoutPopup,
} from "../../services/firebase";
const WIZECARE_GREEN = "#0AAE8A";
const LOGIN_FONT_FAMILY = "'Heebo', sans-serif";
const RTL_LANGUAGES = ["he", "ar"];

/**
 * Microsoft org sign-out flow for switching SSO user. Shown when `logoutUrl` is set.
 */
const SsoSwitchUserModal = ({ logoutUrl, onClose, onAfterSsoCleared, t, language }) => {
  if (!logoutUrl) {
    return null;
  }

  const isRtlLanguage = RTL_LANGUAGES.includes(language);

  const handleOpenLogoutPopup = () => {
    const popup = openMicrosoftLogoutPopup(logoutUrl);
    if (!popup) {
      return;
    }

    const expectedOrigin = window.location.origin;
    let handled = false;
    const finalizeAfterLogout = () => {
      clearSsoAccountState(() => {
        if (typeof onAfterSsoCleared === "function") {
          onAfterSsoCleared();
        }
      });
    };
    const poll = window.setInterval(() => {
      if (handled) {
        window.clearInterval(poll);
        return;
      }
      if (popup.closed) {
        handled = true;
        window.clearInterval(poll);
        finalizeAfterLogout();
        return;
      }
      try {
        if (popup.location.origin === expectedOrigin) {
          handled = true;
          window.clearInterval(poll);
          popup.close();
          finalizeAfterLogout();
        }
      } catch (e) {
        // Still on Microsoft origin; ignore and keep polling.
      }
    }, 500);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        backgroundColor: "rgba(0, 0, 0, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "min(360px, 100%)",
          maxHeight: "min(85vh, 660px)",
          backgroundColor: "#fff",
          borderRadius: "16px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          fontFamily: LOGIN_FONT_FAMILY,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "24px 20px 18px",
            borderBottom: "1px solid #eee",
            position: "relative",
          }}
        >
          <div
            style={{
              width: "78px",
              height: "78px",
              borderRadius: "22px",
              background: WIZECARE_GREEN,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: "34px",
              fontWeight: 700,
              boxShadow: "0 8px 20px rgba(10, 174, 138, 0.35)",
              marginBottom: "14px",
            }}
          >
            <svg
              width="34"
              height="34"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle cx="12" cy="8" r="4" fill="#FFFFFF" />
              <path d="M4 20C4 16.6863 7.58172 14 12 14C16.4183 14 20 16.6863 20 20" fill="#FFFFFF" />
            </svg>
          </div>
          <span style={{ fontSize: "20px", fontWeight: 700, color: "#1a2b4b", lineHeight: 1 }}>
            {t("login.settings.switchingTitle")}
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: "18px",
              lineHeight: 1,
              cursor: "pointer",
              color: "#666",
              padding: "4px 8px",
              position: "absolute",
              right: "10px",
              top: "10px",
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div
          style={{
            flex: 1,
            minHeight: "155px",
            background: "#f7f7f7",
            padding: "24px 26px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              color: "#333",
              fontSize: "17px",
              lineHeight: 1.65,
              textAlign: isRtlLanguage ? "right" : "center",
              direction: isRtlLanguage ? "rtl" : "ltr",
              maxWidth: "470px",
              fontWeight: 400,
            }}
          >
            <p style={{ margin: 0 }}>
              {t("login.settings.switchAccountStep2Prefix")}
              <strong>{t("login.settings.switchAccountStep2Bold")}</strong>
              {t("login.settings.switchAccountStep2Suffix")}
            </p>
          </div>
        </div>
        <div
          style={{
            padding: "20px 24px 14px",
            borderTop: "1px solid #eee",
            fontSize: "12px",
            color: "#555",
            textAlign: "center",
          }}
        >
          <button
            type="button"
            onClick={handleOpenLogoutPopup}
            style={{
              padding: "17px 20px",
              borderRadius: "18px",
              border: "none",
              width: "100%",
              background: WIZECARE_GREEN,
              color: "#fff",
              fontFamily: LOGIN_FONT_FAMILY,
              fontWeight: 600,
              cursor: "pointer",
              fontSize: "16px",
              boxShadow: "0 8px 18px rgba(10, 174, 138, 0.25)",
              textAlign: "center",
            }}
          >
            {t("login.settings.signOutAndContinue")}
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              marginTop: "14px",
              border: "none",
              background: "transparent",
              color: "#9CA3AF",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: 600,
              fontFamily: LOGIN_FONT_FAMILY,
            }}
          >
            {t("login.settings.goBackToLogin")}
          </button>
        </div>
        <div
          style={{
            padding: "16px 24px 22px",
            borderTop: "1px solid #eee",
            color: "#6b7280",
            fontSize: "12px",
            textAlign: "left",
            lineHeight: 1.45,
            fontFamily: LOGIN_FONT_FAMILY,
          }}
        >
          {t("login.settings.switchAccountFooter")}
        </div>
      </div>
    </div>
  );
};

export default translate("login")(SsoSwitchUserModal);
